import { AccountBrand, Website } from '@betterfunnel/types';
import AlertModal from '@betterfunnel/ui-core/AlertModal';
import Autocomplete from '@betterfunnel/ui-core/Autocomplete';
import Button from '@betterfunnel/ui-core/Button';
import ColorPickerField from '@betterfunnel/ui-core/ColorPickerField';
import TextField from '@betterfunnel/ui-core/TextField';
import Toggle from '@betterfunnel/ui-core/Toggle';
import { useCallback, useContext, useEffect, useState } from 'react';
import { DashboardContext } from '../../contexts/DashboardContext';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import {
  WebsiteUpdate,
  WebsiteUpdaterContext,
} from '../../contexts/WebsiteUpdaterContext';
import { ALLOWED_FONTS } from '../../screens/Brand';
import { CSSTextArea } from '../FormFields/CSSTextArea';
import FileUploadInput from '../FormFields/FileUpload/FileUploadInput';
import SettingSectionTitle from '../settings/SettingSectionTitle';

interface Props {
  websiteId: string;
  website?: Website;
  onUpdateWebsiteEvent: (_: WebsiteUpdate) => void;
}

export default function ManageWebsiteStyle({
  websiteId,
  website,
  onUpdateWebsiteEvent,
}: Props) {
  const { show } = useContext(SnackbarContext);
  const { selectedDynamicPage, accountBrand } = useContext(DashboardContext);
  const { createWebsiteUpdate } = useContext(WebsiteUpdaterContext);

  const [showResetModal, setShowResetModal] = useState<{
    type: 'account' | 'dynamicPage';
  }>();

  const [brand, setBrand] = useState<AccountBrand | undefined>(
    website?.development.brand,
  );

  useEffect(() => {
    const brand = website?.development.brand;

    setBrand({
      ...accountBrand,
      ...selectedDynamicPage?.data().brand,
      ...brand,
    });
  }, [accountBrand, selectedDynamicPage, website?.development.brand]);

  const updateWebsite = useCallback(
    async (data: AccountBrand, reset = false) => {
      if (!selectedDynamicPage) return;
      try {
        const wu = createWebsiteUpdate(
          selectedDynamicPage.id,
          websiteId,
          '',
          '',
          data,
          reset ? 'resetBrand' : 'editBrand',
          [],
          website?.development
            ? JSON.parse(JSON.stringify(website?.development))
            : undefined,
        );
        if (!wu) return;

        onUpdateWebsiteEvent(wu);
        show('Updated');
      } catch (_) {
        show('Failed to update');
      }
    },
    [
      createWebsiteUpdate,
      onUpdateWebsiteEvent,
      selectedDynamicPage,
      show,
      website?.development,
      websiteId,
    ],
  );

  return (
    <div className="max-w-[360px]">
      <SettingSectionTitle title="Logo" description="Add your logo." />

      <div className="mb-6">
        <dl className="mt-6 space-y-6 border-t border-gray-200 text-sm leading-6">
          <div className="flex w-full flex-col pt-6">
            <div className="mt-2 w-full max-w-[360px]">
              <FileUploadInput
                image={brand?.logo ?? accountBrand?.logo}
                onSelected={(image) => {
                  updateWebsite({
                    logo: (image ?? null) as any,
                  });
                }}
              />
            </div>
          </div>
        </dl>
      </div>

      <div className="mx-auto max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none">
        <div>
          <h2 className="text-base leading-7 font-semibold text-gray-900">
            Color Pallet
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom button styling to your landing pages.
          </p>

          <dl className="mt-6 space-y-6 border-t border-gray-200 text-sm leading-6">
            <div className="pt-6 sm:flex">
              <dt className="w-full grow font-medium text-gray-900">
                Primary Color
              </dt>
              <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <ColorPickerField
                  value={brand?.primaryColor ?? accountBrand?.primaryColor}
                  debounce
                  onChange={(v) => {
                    updateWebsite({ primaryColor: v });
                  }}
                />
              </dd>
            </div>
            <div className="pt-6 sm:flex">
              <dt className="w-full grow font-medium text-gray-900">
                Secondary Color
              </dt>
              <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <ColorPickerField
                  value={brand?.secondaryColor ?? accountBrand?.secondaryColor}
                  debounce
                  onChange={(v) => updateWebsite({ secondaryColor: v })}
                />
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h2 className="text-base leading-7 font-semibold text-gray-900">
            Buttons
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom button styling to your landing pages.
          </p>

          <dl className="mt-6 flex flex-col gap-2 space-y-6 border-t border-gray-200 text-sm leading-6">
            <div className="flex flex-col gap-2 pt-6">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Custom Button CSS
              </dt>
              <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <CSSTextArea
                  debounce
                  className="min-h-36 w-full"
                  value={brand?.buttonCSS}
                  onChange={(newButtonCSS) => {
                    updateWebsite({ buttonCSS: newButtonCSS });
                  }}
                />
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h2 className="text-base leading-7 font-semibold text-gray-900">
            Font
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom font to your landing pages.
          </p>

          <dl className="mt-6 space-y-6 border-t border-gray-200 text-sm leading-6">
            <div className="flex flex-col gap-2 pt-6">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Font
              </dt>
              <dd className="mt-1 flex items-center justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <Autocomplete
                  selected={brand?.font ?? accountBrand?.font ?? 'Inter'}
                  options={ALLOWED_FONTS}
                  onChange={(selected) => {
                    updateWebsite({ font: selected ?? undefined });
                  }}
                />
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h2 className="text-base leading-7 font-semibold text-gray-900">
            Border Radius
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom border radius to your landing pages.
          </p>

          <dl className="mt-6 space-y-6 border-t border-gray-200 text-sm leading-6">
            <div className="flex flex-col gap-2 pt-6 sm:flex">
              <dd className="mt-1 flex items-center gap-x-6 sm:mt-0 sm:flex-auto">
                <TextField
                  className="h-16 max-w-[64px] text-center"
                  style={{
                    borderRadius:
                      brand?.borderRadius ?? accountBrand?.borderRadius ?? 6,
                  }}
                  value={`${brand?.borderRadius ?? accountBrand?.borderRadius ?? 6}`}
                  onChange={(e) => {
                    const newVal = e.target.value;

                    try {
                      const num = parseFloat(newVal || '0');
                      updateWebsite({ borderRadius: num });
                    } catch (_) {
                      /* empty */
                    }
                  }}
                />
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h2 className="text-base leading-7 font-semibold text-gray-900">
            Shadow
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom shadow to your landing pages.
          </p>

          <dl className="mt-6 mb-3 space-y-6 border-t border-gray-200 text-sm leading-6">
            <div className="w-full pt-6 sm:flex">
              <dt className="grow font-medium text-gray-900">Shadow</dt>
              <dd className="flex justify-end">
                <Toggle
                  checked={brand?.shadow ?? accountBrand?.shadow ?? true}
                  onChange={(checked) => {
                    updateWebsite({ shadow: checked });
                  }}
                />
              </dd>
            </div>
          </dl>
        </div>

        <div className="pb-10">
          <h2 className="text-base leading-7 font-semibold text-gray-900">
            Reset
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Reset config to default.
          </p>

          <dl className="mt-6 mb-3 flex flex-col items-start justify-start gap-2 border-t border-gray-200 pt-4 text-sm leading-6">
            <Button
              variant="ternary"
              color="primary"
              onClick={() => setShowResetModal({ type: 'account' })}
            >
              Reset to Account Defaults
            </Button>

            <Button
              variant="ternary"
              color="primary"
              onClick={() => setShowResetModal({ type: 'dynamicPage' })}
            >
              Reset to Dynamic Page Defaults
            </Button>
          </dl>
        </div>
      </div>

      <AlertModal
        title={`Reset to ${showResetModal?.type === 'account' ? 'Account' : 'Dynamic Page'} Defaults`}
        open={!!showResetModal}
        onClose={() => setShowResetModal(undefined)}
        actions={[
          {
            type: 'secondary',
            title: 'Cancel',
            onClick: () => setShowResetModal(undefined),
          },
          {
            type: 'primary',
            color: 'negative',
            title: 'Reset',
            onClick: () => {
              if (showResetModal?.type === 'account') {
                updateWebsite(accountBrand ?? {}, true);
              } else {
                updateWebsite({}, true);
              }
              setShowResetModal(undefined);
            },
          },
        ]}
        type={'warn'}
      />
    </div>
  );
}
